import type { ReactNode } from 'react';
import { parseDescription } from './descriptionBlocks';

/**
 * An event description, with its sections rendered as sections.
 *
 * <p>It used to be one pre-wrapped block of text, so "Format" and "What the organiser requires"
 * were indistinguishable from the lines under them — the reader had to parse the shape themselves
 * every time. Real headings and real lists mean a screen reader announces "list, five items" and
 * someone scanning for whether they're eligible can find that block without reading the rest.
 *
 * <p>Built as elements, never as HTML — descriptions are partly organiser-supplied, and nothing
 * here interprets markup.
 */
export function EventDescription({ description }: { description: string }): ReactNode {
  const blocks = parseDescription(description);

  return (
    <div className="prose">
      {blocks.map((block, index) => (
        <section className="prose__block" key={`${block.heading ?? 'text'}-${index}`}>
          {/* h3: the section this sits in already has "About this event" as its h2. */}
          {block.heading && <h3 className="prose__heading">{block.heading}</h3>}
          {block.paragraphs.map((paragraph, i) => (
            <p className="prose__paragraph" key={i}>
              {paragraph}
            </p>
          ))}
          {block.bullets.length > 0 && (
            <ul className="prose__list">
              {block.bullets.map((bullet, i) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
